import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Paper,
  Typography,
  Button,
  Alert,
  CircularProgress,
} from '@mui/material';
import ReferenceManagement from '../shared/ReferenceManagement';
import api from '../../utils/api';

const References: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [counsellorData, setCounsellorData] = useState<any>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const fetchCounsellorData = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get(`/api/counsellors/${id}`);
      
      if (response.status === 200) {
        setCounsellorData(response.data);
      }
    } catch (err: any) {
      console.error('Error fetching counsellor data:', err);
      setError('Failed to load counsellor data');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (id) {
      fetchCounsellorData();
    }
  }, [id, fetchCounsellorData]);

  const handleRefresh = () => {
    fetchCounsellorData();
    setSuccess('References updated successfully');
  };

  if (loading) {
    return (
      <Container maxWidth="md">
        <Box sx={{ mt: 4, display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error && !counsellorData) {
    return (
      <Container maxWidth="md">
        <Box sx={{ mt: 4 }}>
          <Alert severity="error">{error}</Alert>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg"> {/* Changed from md to lg for wider container */}
      <Box sx={{ mt: 4, mb: 4 }}>
        <Paper elevation={3} sx={{ p: 4 }}>
          <Typography variant="h4" component="h1" gutterBottom align="center">
            Manage References
          </Typography>
          <Typography variant="body1" paragraph>
            Please provide references to proceed with your onboarding process.
          </Typography>
          
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          
          {success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {success}
            </Alert>
          )}

          {counsellorData && (
            <Box sx={{ mx: -2 }}> {/* Negative margin to allow full width within the Paper */}
              <ReferenceManagement 
                counsellorId={id || ''}
                counsellorData={counsellorData}
                onRefresh={handleRefresh}
                isAdmin={false}
              />
            </Box>
          )}

          <Box sx={{ mt: 4, display: 'flex', justifyContent: 'space-between' }}>
            <Button
              variant="outlined"
              color="primary"
              onClick={() => navigate('/counsellor/onboarding')}
            >
              Back to Onboarding
            </Button>
          </Box>
        </Paper>
      </Box>
    </Container>
  );
};

export default References;
